/**
 * Service Worker registration for Lauft Pro.
 *
 * Registers the SW, detects when a new version is waiting, and dispatches
 * a custom event ('lauftpro-sw-update-ready') that the app listens for to
 * show the "New version available" banner.
 *
 * Usage in src/index.js:
 *   import { registerServiceWorker } from './serviceWorkerRegistration';
 *   registerServiceWorker();
 *
 * Usage in any component:
 *   useEffect(() => {
 *     const handler = (e) => setUpdateReady(true);
 *     window.addEventListener('lauftpro-sw-update-ready', handler);
 *     return () => window.removeEventListener('lauftpro-sw-update-ready', handler);
 *   }, []);
 */

let waitingWorker = null;

export function registerServiceWorker() {
  // Only register in production. Service workers in development cause
  // confusing caching issues during local work.
  if (process.env.NODE_ENV !== 'production') {
    console.log('[SW] skipping registration in development');
    return;
  }

  if (!('serviceWorker' in navigator)) {
    console.log('[SW] not supported in this browser');
    return;
  }

  // Wait for window load so SW registration doesn't compete with critical
  // page resources during the first paint.
  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

    navigator.serviceWorker
      .register(swUrl)
      .then((registration) => {
        console.log('[SW] registered successfully');

        // Check for updates every 60 minutes for users who keep the PWA open
        // for long sessions. (Browsers also auto-check on navigation.)
        setInterval(() => {
          registration.update().catch(() => {});
        }, 60 * 60 * 1000);

        // If a worker is already waiting (user closed the PWA mid-update),
        // notify the app immediately on next launch.
        if (registration.waiting) {
          waitingWorker = registration.waiting;
          dispatchUpdateReady();
        }

        // When a new SW is found, watch its lifecycle. Once it reaches
        // 'installed' state with an active controller, that means a new
        // version is ready to take over.
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (
              newWorker.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              waitingWorker = newWorker;
              dispatchUpdateReady();
            }
          });
        });
      })
      .catch((err) => {
        console.error('[SW] registration failed:', err);
      });

    // When the new SW takes control (after user taps "Refresh"), reload the
    // page so they get the new version's UI.
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}

function dispatchUpdateReady() {
  console.log('[SW] new version ready');
  window.dispatchEvent(new CustomEvent('lauftpro-sw-update-ready'));
}

/**
 * Called by the "Refresh" button in the update banner.
 * Tells the waiting SW to skipWaiting and take control.
 * The 'controllerchange' listener above then triggers a page reload.
 */
export function applyServiceWorkerUpdate() {
  if (waitingWorker) {
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  }
}

/**
 * Optional: detect whether the PWA is currently running in standalone mode
 * (i.e., installed to home screen) vs. a regular browser tab.
 * Useful for showing/hiding the install prompt.
 */
export function isPWAStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

/**
 * Manually unregister the SW. Useful for support/debugging if an agent's
 * cache gets stuck. Call from a hidden settings option.
 */
export function unregisterServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .then(() => window.location.reload())
      .catch((err) => console.error('[SW] unregister failed:', err));
  }
}